/**
 * In→out camera traverse pack for sim-lab.
 * Blueprint: docs/MODEL_FEATURE_MAP.md
 */

import { DEFAULT_FEATURE_VISIBILITY } from './modelFeatureMap';

const DEFAULT_SETTLE_MS = 1100;

/**
 * Build the in-out-v0 stop list from a localize result.
 * @param {object} localize — from localizeFromBox3
 */
export function buildInOutTraverse(localize) {
  if (!localize?.presets) return [];

  const floorY = Number.isFinite(localize.floorY) ? localize.floorY : 0;
  const entry = localize.entry;
  const center = localize.center || [0, 1.5, 0];
  const outward = entry?.outward || [0, 0, 1];
  const point = entry?.point || [center[0], floorY + 1.5, center[2]];

  const interiorEye = [
    point[0] - outward[0] * 1.25,
    floorY + 1.55,
    point[2] - outward[2] * 1.25,
  ];
  const interiorLook = [center[0], floorY + 1.45, center[2]];

  const restoreVis = { ...DEFAULT_FEATURE_VISIBILITY };
  const doorsOff = { ...DEFAULT_FEATURE_VISIBILITY, door: false };

  return [
    {
      id: 'approach',
      label: 'Approach',
      eye: localize.presets.approach.eye,
      lookAt: localize.presets.approach.lookAt,
      featureVisibility: restoreVis,
      layers: { roof: true, walls: true, interior: true },
      settleMs: DEFAULT_SETTLE_MS,
    },
    {
      id: 'entry',
      label: 'Entry',
      eye: localize.presets.entry.eye,
      lookAt: localize.presets.entry.lookAt,
      featureVisibility: doorsOff,
      layers: { roof: true, walls: true, interior: true },
      settleMs: DEFAULT_SETTLE_MS,
    },
    {
      id: 'interior',
      label: 'Interior',
      eye: interiorEye,
      lookAt: interiorLook,
      featureVisibility: doorsOff,
      layers: { roof: false, walls: true, interior: true },
      settleMs: DEFAULT_SETTLE_MS + 200,
    },
    {
      id: 'exit',
      label: 'Exit',
      eye: localize.presets.iso.eye,
      lookAt: localize.presets.iso.lookAt,
      featureVisibility: restoreVis,
      layers: { roof: true, walls: true, interior: true },
      settleMs: DEFAULT_SETTLE_MS,
    },
  ];
}

/**
 * Run stops sequentially against a LabViewer-style API.
 * @param {object} api — { setLookAt, waitSettled? }
 * @param {object[]} stops
 * @param {{
 *   onStop?: (stop, index) => void,
 *   onFeatureVisibility?: (vis) => void,
 *   onLayers?: (layers) => void,
 *   signal?: { cancelled: boolean },
 * }} [hooks]
 */
export async function playTraverseStops(api, stops, hooks = {}) {
  const { onStop, onFeatureVisibility, onLayers, signal } = hooks;
  if (!api?.setLookAt || !stops?.length) return;

  for (let i = 0; i < stops.length; i += 1) {
    if (signal?.cancelled) return;
    const stop = stops[i];
    onStop?.(stop, i);
    if (stop.featureVisibility) onFeatureVisibility?.(stop.featureVisibility);
    if (stop.layers) onLayers?.(stop.layers);
    const [ex, ey, ez] = stop.eye;
    const [lx, ly, lz] = stop.lookAt;
    api.setLookAt(ex, ey, ez, lx, ly, lz, true);
    const wait = stop.settleMs ?? DEFAULT_SETTLE_MS;
    if (api.waitSettled) {
      await api.waitSettled();
      await sleep(Math.max(200, wait - 200));
    } else {
      await sleep(wait);
    }
  }
}

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}
